import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/1jxg5966_grief%20coaching%20logo.png";

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Programs', 
      path: '/programs',
      submenu: [
        { name: 'All Programs', path: '/programs' },
        { name: 'Foundations Introduction', path: '/programs/foundations-introduction' },
        { name: 'Foundations Course', path: '/programs/foundations-course' },
        { name: 'Workshop Series', path: '/programs/workshop-series' },
        { name: 'Intensive Experience', path: '/programs/intensive' },
        { name: 'Cohort Experience', path: '/programs/cohort' },
      ]
    },
    { name: 'Speaking', path: '/speaking' },
    { name: 'Contact', path: 'https://griefassessment.agcthefixer.info/contact-724806', external: true },
  ];

  const isActive = (path) => location.pathname === path;

  const MobileMenu = () => {
    if (!isMobileMenuOpen) return null;
    
    return createPortal(
      <>
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden"
          style={{ zIndex: 99998 }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        {/* Menu Panel */}
        <div 
          className="fixed top-0 right-0 bottom-0 w-[85%] max-w-[320px] bg-white p-8 shadow-2xl lg:hidden overflow-y-auto"
          style={{ zIndex: 99999 }}
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            {/* Close button */}
            <div className="flex justify-between items-center mb-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                <img 
                  src={LOGO_URL} 
                  alt="Dr. Angelia Griffin" 
                  className="h-12 w-auto"
                />
              </Link>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-[#1B365D]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {navLinks.map((link) => (
              link.submenu ? (
                <div key={link.name} className="flex flex-col">
                  <span className="text-sm font-bold uppercase tracking-wide text-[#C9A962] mb-2">
                    {link.name}
                  </span>
                  {link.submenu.map((sublink) => (
                    <Link
                      key={sublink.name}
                      to={sublink.path}
                      className={`py-2 pl-4 text-[#1B365D] hover:text-[#2D7D7B] ${isActive(sublink.path) ? 'text-[#2D7D7B]' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {sublink.name}
                    </Link>
                  ))}
                </div>
              ) : link.external ? (
                <a
                  key={link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2 text-lg font-medium text-[#1B365D] hover:text-[#2D7D7B]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`py-2 text-lg font-medium text-[#1B365D] hover:text-[#2D7D7B] ${isActive(link.path) ? 'text-[#2D7D7B]' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-[#C9A962]/30" data-testid="main-navigation">
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="Dr. Angelia Griffin Grief Coaching" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.submenu ? (
                  <div 
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button 
                      className={`nav-link flex items-center gap-1 ${isActive(link.path) || location.pathname.startsWith('/programs') ? 'text-[#2D7D7B]' : ''}`}
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isServicesOpen && (
                      <div className="absolute top-full left-0 pt-2 animate-fade-in hidden lg:block">
                        <div className="bg-white rounded-xl shadow-lg border border-[#C9A962]/20 py-2 min-w-[280px]">
                          {link.submenu.map((sublink) => (
                            <Link
                              key={sublink.name}
                              to={sublink.path}
                              className={`block px-4 py-2.5 text-sm hover:bg-[#F5F5F0] hover:text-[#2D7D7B] transition-colors ${isActive(sublink.path) ? 'text-[#2D7D7B] bg-[#F5F5F0]' : 'text-[#1B365D]'}`}
                              data-testid={`nav-sublink-${sublink.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {sublink.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : link.external ? (
                  <a
                    key={link.name}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link"
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? 'text-[#2D7D7B]' : ''}`}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-[#1B365D]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>
      
      {/* Mobile Menu Portal */}
      <MobileMenu />
    </>
  );
};
